"use client";

import React from "react";
import { Mail, Plus, Trash2, AlertCircle, X } from "lucide-react";

interface EmailAliasesEditorPanelProps {
  showEditorDirectly: boolean;
  contactEmail: string;
  aliases: string[];
  newAlias: string;
  onAliasChange: (val: string) => void;
  validationError: string | null;
  isAdding: boolean;
  isPending: boolean;
  handleAddAlias: () => void;
  handleRemoveAlias: (alias: string) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleOpenChange: (open: boolean) => void;
}

export function EmailAliasesEditorPanel({
  showEditorDirectly,
  contactEmail,
  aliases,
  newAlias,
  onAliasChange,
  validationError,
  isAdding,
  isPending,
  handleAddAlias,
  handleRemoveAlias,
  handleKeyPress,
  handleOpenChange,
}: EmailAliasesEditorPanelProps) {
  return (
    <div className="space-y-4 p-4">
      {!showEditorDirectly && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Addresses
          </h3>
          <button type="button"
            onClick={() => handleOpenChange(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {!showEditorDirectly && (
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800">
          <p className="text-xs text-blue-600 font-medium mb-1 dark:text-blue-400">Primary</p>
          <p className="text-sm font-medium text-card-foreground">{contactEmail}</p>
        </div>
      )}

      {aliases.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Alternatives</p>
          <div className={showEditorDirectly ? "space-y-2" : "space-y-2 max-h-48 overflow-y-auto"}>
            {aliases.map((alias) => (
              <div
                key={alias}
                className="flex items-center justify-between bg-card-50 rounded-lg p-3 border border-gray-200 hover:border-gray-300 transition-colors dark:border-gray-700"
              >
                <p className="text-sm text-card-foreground truncate flex-1">{alias}</p>
                <button type="button"
                  onClick={() => handleRemoveAlias(alias)}
                  disabled={isPending}
                  className="ml-2 text-red-600 hover:text-red-700 disabled:opacity-50 flex-shrink-0"
                  title="Remove email"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {aliases.length < 10 && (
        <div
          className={showEditorDirectly ? "space-y-2" : "space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700"}
        >
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Add Alternative</p>
          <div className="flex gap-2">
            <input
              type="email"
              value={newAlias}
              onChange={(e) => onAliasChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter email"
              disabled={isAdding || isPending}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
              autoFocus
            />
            <button type="button"
              onClick={handleAddAlias}
              disabled={isAdding || isPending || !newAlias.trim()}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-card-300 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          {validationError && (
            <div className="flex items-center gap-2 text-red-600 text-xs">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span>{validationError}</span>
            </div>
          )}

          <p className="text-xs text-gray-500">{10 - aliases.length} more allowed</p>
        </div>
      )}

      {aliases.length === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No alternative emails yet</p>
          <p className="text-xs text-gray-400 mt-1">Add one to get started</p>
        </div>
      )}
    </div>
  );
}
